import React, { useCallback, useEffect, useState } from 'react'
import { dnsAPI } from '@hpanel/web-common/api/client'
import type { DnsDelegation, DnsDelegationRecord } from '@hpanel/web-common/api/types'
import { AlertTriangle, Check, Copy, RefreshCw } from 'lucide-react'

/**
 * DnsHostingSection — on the domain Manage page.
 *
 * Shows whether the domain's DNS is delegated to this panel's nameservers.
 * If not, lists the records to publish at the external provider (with a Copy
 * button each) and, after "Check now", the live public answer per record.
 */

const cardClass =
  'bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-5'
const actionButtonClass =
  'inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-800 text-[13px] font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'

// navigator.clipboard needs a secure context; fall back to execCommand on
// plain-HTTP dev/test surfaces.
const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (e) {
    console.error('[DnsHostingSection] navigator.clipboard failed', e)
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch (e) {
    console.error('[DnsHostingSection] execCommand copy failed', e)
    return false
  }
}

const fmtChecked = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

const errMessage = (e: unknown, fallback: string): string =>
  e instanceof Error && e.message ? e.message : fallback

export const DnsHostingSection: React.FC<{ domainId: number }> = ({ domainId }) => {
  const [data, setData] = useState<DnsDelegation | null>(null)
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<number | null>(null)

  const load = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      setData(await dnsAPI.delegation(domainId))
    } catch (e: unknown) {
      console.error('Loading DNS delegation failed', e)
      setError(errMessage(e, 'Failed to load the DNS hosting status.'))
    } finally {
      setLoading(false)
    }
  }, [domainId])

  useEffect(() => {
    void load()
  }, [load])

  const check = async () => {
    setError(null)
    setChecking(true)
    try {
      setData(await dnsAPI.checkDelegation(domainId))
    } catch (e: unknown) {
      console.error('DNS delegation check failed', e)
      setError(errMessage(e, 'The DNS check failed.'))
    } finally {
      setChecking(false)
    }
  }

  const copy = async (i: number, value: string) => {
    if (await copyText(value)) {
      setCopied(i)
      window.setTimeout(() => setCopied((c) => (c === i ? null : c)), 2000)
    } else {
      setError('Could not copy to the clipboard; select the value and copy it manually.')
    }
  }

  const delegated = data?.state === 'delegated'

  const nsList = (testId: string, label: string, names: string[]) => (
    <p className="text-[13px] text-gray-700 dark:text-gray-300">
      {label}{' '}
      <span data-testid={testId} className="font-mono text-[12px]">
        {names.length ? names.join(', ') : 'none'}
      </span>
    </p>
  )

  const recordRow = (r: DnsDelegationRecord, i: number) => {
    const checked = r.live !== null
    return (
      <tr
        key={i}
        data-testid={`dns-hosting-record-${i}`}
        className="border-t border-gray-100 dark:border-gray-800 align-top"
      >
        <td className="py-1.5 pr-2 font-medium">{r.type}</td>
        <td className="py-1.5 pr-2 font-mono text-[12px] break-all">{r.name}</td>
        <td className="py-1.5 pr-2 font-mono text-[12px] break-all">
          {r.value}
          {r.dkim_mismatch && (
            <div
              data-testid={`dns-hosting-dkim-warning-${i}`}
              role="alert"
              className="mt-1 flex items-start gap-1.5 font-sans text-[11px] text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
              <span>
                The published DKIM key does not match this one (for example extra characters after
                the key). Replace the record with the value above.
              </span>
            </div>
          )}
          {checked && (
            <div
              data-testid={`dns-hosting-live-${i}`}
              className="mt-1 font-sans text-[11px] text-gray-500 dark:text-gray-400"
            >
              Live: {r.live!.length ? r.live!.join(' | ') : 'no answer'}
            </div>
          )}
        </td>
        <td className="py-1.5 pr-2">{r.priority ?? '—'}</td>
        <td className="py-1.5 pr-2">{r.ttl}</td>
        <td className="py-1.5 pr-2 whitespace-nowrap">
          {checked && (
            <span
              data-testid={`dns-hosting-match-${i}`}
              className={`mr-2 text-[11px] font-medium ${
                r.matches ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
              }`}
            >
              {r.matches ? 'Match' : 'Mismatch'}
            </span>
          )}
          <button
            type="button"
            onClick={() => void copy(i, r.value)}
            data-testid={`dns-hosting-copy-${i}`}
            aria-label={`Copy ${r.type} value`}
            className="inline-flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-400 hover:underline"
          >
            <Copy className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" />
            Copy
          </button>
          {copied === i && (
            <span
              data-testid={`dns-hosting-copied-${i}`}
              role="status"
              className="ml-1.5 inline-flex items-center gap-0.5 text-[11px] text-green-700 dark:text-green-300"
            >
              <Check className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" />
              Copied
            </span>
          )}
        </td>
      </tr>
    )
  }

  return (
    <div data-testid="dns-hosting" className={cardClass}>
      <h2 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-3">DNS hosting</h2>

      {error && (
        <div
          data-testid="dns-hosting-error"
          role="alert"
          className="mb-3 flex items-start gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-[12px] text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {loading && !data && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading DNS status...</p>
      )}

      {data && (
        <div className="space-y-3">
          {delegated ? (
            <>
              <p data-testid="dns-hosting-state" className="text-[13px] font-medium text-green-700 dark:text-green-300">
                DNS is hosted by this panel
              </p>
              {nsList('dns-hosting-ns-expected', 'Nameservers:', data.nameservers_expected)}
            </>
          ) : (
            <>
              <p data-testid="dns-hosting-state" className="text-[13px] text-gray-900 dark:text-gray-100">
                {data.state === 'partial'
                  ? 'Some, but not all, of the nameservers point at this panel. '
                  : ''}
                Your DNS is hosted elsewhere. Add these records at your provider
              </p>
              {data.state === 'partial' && (
                <>
                  {nsList('dns-hosting-ns-found', 'Found:', data.nameservers_found)}
                  {nsList('dns-hosting-ns-expected', 'Expected:', data.nameservers_expected)}
                </>
              )}
              {data.detail && (
                <p data-testid="dns-hosting-detail" className="text-[12px] text-gray-600 dark:text-gray-400">
                  {data.detail}
                </p>
              )}
              {data.records.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[13px] text-gray-900 dark:text-gray-100">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="pr-2 font-medium">Type</th>
                        <th className="pr-2 font-medium">Name</th>
                        <th className="pr-2 font-medium">Value</th>
                        <th className="pr-2 font-medium">Priority</th>
                        <th className="pr-2 font-medium">TTL</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>{data.records.map(recordRow)}</tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {data.checked_at && (
            <p data-testid="dns-hosting-checked-at" className="text-[11px] text-gray-500 dark:text-gray-400">
              Checked {fmtChecked(data.checked_at)}
            </p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => void check()}
        disabled={checking}
        data-testid="dns-hosting-check"
        className={`${actionButtonClass} mt-3`}
      >
        <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
        {checking ? 'Checking…' : 'Check now'}
      </button>
    </div>
  )
}
